<template>
  <div class="p-6 space-y-4 animate-fade-in">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-white text-xl font-semibold">Inventory</h2>
        <p class="text-slate-400 text-sm">Overview of equipment types and availability</p>
      </div>
    </div>

    <div v-if="error" class="text-red-400 text-sm">{{ error }}</div>

    <div v-if="typesLoading" class="text-slate-300 text-sm">Loading equipment…</div>
    <div v-else-if="!types.length" class="text-slate-400 text-sm">No equipment types yet.</div>

    <div v-else class="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-6">
      <button
        v-for="t in types"
        :key="t.key"
        type="button"
        class="panel-neo panel-neo--glassier p-5 text-left transition ring-offset-2 focus:outline-none focus:ring"
        :class="{ 'ring-2 ring-primary-400': active && active.id === t.id }"
        @click="open(t)"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <div class="text-white font-semibold text-base">{{ t.name }}</div>
            <div class="text-slate-400 text-xs">{{ t.make }} • {{ t.model }}</div>
            <div class="text-slate-500 text-xs mt-1" v-if="t.descriptor">{{ t.descriptor }}</div>
          </div>
          <span
            v-if="t.isAccessory"
            class="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-slate-800 text-slate-200"
            >Accessory</span
          >
        </div>
        <div class="mt-4 text-xs grid grid-cols-2 gap-2">
          <div class="px-2 py-1 rounded border border-green-700/40 bg-green-500/10 text-green-300">
            Available {{ t.counts.available }}
          </div>
          <div class="px-2 py-1 rounded border border-blue-700/40 bg-blue-500/10 text-blue-300">
            Checked Out {{ t.counts.checkedOut }}
          </div>
          <div class="px-2 py-1 rounded border border-amber-700/40 bg-amber-500/10 text-amber-300">
            Reserved {{ t.counts.reserved }}
          </div>
          <div class="px-2 py-1 rounded border border-red-700/40 bg-red-500/10 text-red-300">
            Maintenance {{ t.counts.maintenance }}
          </div>
        </div>
      </button>
    </div>

    <Motion
      v-if="active"
      class="fixed inset-0 bg-black/40 backdrop-blur z-40"
      :initial="{ opacity: 0 }"
      :animate="{ opacity: 1 }"
      :exit="{ opacity: 0 }"
      :transition="{ duration: 0.24 }"
      @click="close"
    />

    <Motion
      v-if="active"
      class="fixed inset-y-0 right-0 z-50 w-full max-w-6xl bg-slate-950/95 shadow-2xl border-l border-slate-800 overflow-y-auto"
      :initial="{ x: '100%' }"
      :animate="{ x: 0 }"
      :exit="{ x: '100%' }"
      :transition="{ duration: 0.32 }"
    >
      <div class="flex flex-col h-full">
        <header class="flex items-start justify-between gap-6 px-8 py-6 border-b border-slate-800">
          <div>
            <div class="text-xs text-slate-400 uppercase tracking-wide">Equipment Type</div>
            <h3 class="text-2xl font-semibold text-white">{{ active?.name }}</h3>
            <p class="text-slate-400 text-sm mt-1">{{ active?.make }} • {{ active?.model }}</p>
          </div>
          <button
            type="button"
            class="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white"
            @click="close"
          >
            Close
          </button>
        </header>

        <div class="px-8 py-6 space-y-6">
          <div class="grid gap-6 xl:grid-cols-[360px,1fr,340px]">
            <section class="glass-strong rounded-xl p-6 space-y-5">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <h4 class="text-white font-semibold text-sm uppercase tracking-wide">Overview</h4>
                  <p class="text-slate-400 text-xs mt-1" v-if="active?.descriptor">
                    {{ active?.descriptor }}
                  </p>
                </div>
                <button
                  v-if="active"
                  type="button"
                  class="text-xs text-primary-300 hover:text-primary-100"
                  @click="startEditType"
                >
                  Edit
                </button>
              </div>

              <dl class="grid grid-cols-2 gap-3" @dblclick="startEditType">
                <div class="mini-stat mini-stat--available">
                  <dt>Available</dt>
                  <dd>{{ active?.counts.available ?? 0 }}</dd>
                </div>
                <div class="mini-stat mini-stat--checked">
                  <dt>Checked Out</dt>
                  <dd>{{ active?.counts.checkedOut ?? 0 }}</dd>
                </div>
                <div class="mini-stat mini-stat--reserved">
                  <dt>Reserved</dt>
                  <dd>{{ active?.counts.reserved ?? 0 }}</dd>
                </div>
                <div class="mini-stat mini-stat--maintenance">
                  <dt>Maintenance</dt>
                  <dd>{{ active?.counts.maintenance ?? 0 }}</dd>
                </div>
                <div class="mini-stat col-span-2 mini-stat--total">
                  <dt>Total Serialised Units</dt>
                  <dd>{{ active?.counts.total ?? 0 }}</dd>
                </div>
              </dl>

              <div class="grid gap-3 text-xs text-slate-300 border-t border-slate-800 pt-4">
                <div class="flex justify-between">
                  <span class="text-slate-500">Category</span
                  ><span>{{ active?.categoryName || 'Unassigned' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-500">Accessory</span
                  ><span>{{ active?.isAccessory ? 'Yes' : 'No' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-500">Products</span
                  ><span>{{
                    active?.products && active.products.length ? active.products.join(', ') : '—'
                  }}</span>
                </div>
              </div>

              <div
                v-if="editingType"
                class="rounded-lg bg-slate-950/70 border border-slate-800 p-4 space-y-3 text-xs text-slate-200"
              >
                <div class="grid gap-2">
                  <label class="grid gap-1">
                    <span class="uppercase tracking-wide text-[10px] text-slate-500">Name</span>
                    <input v-model="typeForm.name" class="input-dark" />
                  </label>
                  <label class="grid gap-1">
                    <span class="uppercase tracking-wide text-[10px] text-slate-500"
                      >Descriptor</span
                    >
                    <input v-model="typeForm.descriptor" class="input-dark" />
                  </label>
                  <label class="grid gap-1">
                    <span class="uppercase tracking-wide text-[10px] text-slate-500"
                      >Products (comma separated)</span
                    >
                    <input v-model="typeForm.products" class="input-dark" placeholder="POTS Repl" />
                  </label>
                </div>
                <div class="flex items-center gap-3 text-xs">
                  <label class="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      v-model="typeForm.isAccessory"
                      class="form-checkbox form-checkbox--glass"
                    />
                    <span>Accessory</span>
                  </label>
                  <div class="ml-auto space-x-2">
                    <button
                      type="button"
                      class="btn-secondary btn-secondary--xs"
                      @click="cancelEditType"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      class="btn-primary btn-primary--xs"
                      :disabled="savingType"
                      @click="saveType"
                    >
                      {{ savingType ? 'Saving…' : 'Save' }}
                    </button>
                  </div>
                </div>
                <p v-if="typeFormError" class="text-rose-400 text-xs">{{ typeFormError }}</p>
              </div>
            </section>

            <section class="glass-strong rounded-xl p-6 h-full flex flex-col">
              <div class="flex flex-wrap items-center gap-2">
                <h4 class="text-white font-semibold text-sm uppercase tracking-wide">
                  Serial Numbers
                </h4>
                <div class="ml-auto text-xs text-slate-400" v-if="serialsLoading">Loading…</div>
                <div class="ml-auto text-xs text-red-400" v-else-if="serialError">
                  {{ serialError }}
                </div>
              </div>
              <div class="mt-3 flex flex-wrap items-center gap-2 text-xs">
                <button @click="setFilter('all')" :class="chipClass('all')">All</button>
                <button @click="setFilter('available')" :class="chipClass('available')">
                  Available
                </button>
                <button @click="setFilter('reserved')" :class="chipClass('reserved')">
                  Reserved
                </button>
                <button @click="setFilter('checked-out')" :class="chipClass('checked-out')">
                  Checked Out
                </button>
                <button @click="setFilter('maintenance')" :class="chipClass('maintenance')">
                  Maintenance
                </button>
                <input
                  v-model="query"
                  placeholder="Search serial"
                  class="ml-auto px-2 py-1 bg-slate-900/70 border border-slate-700 rounded text-slate-200"
                />
              </div>
              <div class="mt-3 flex flex-wrap items-center gap-3 border-t border-slate-800/80 pt-3 text-xs">
                <label class="inline-flex items-center gap-2 text-slate-300">
                  <input
                    type="checkbox"
                    class="form-checkbox form-checkbox--glass"
                    :checked="allVisibleSerialsSelected"
                    :disabled="!filteredSerialIds.length || deletingSelectedSerials"
                    @change="onSelectAllVisibleChange"
                  />
                  <span>Select all visible</span>
                </label>
                <button
                  type="button"
                  class="btn-secondary btn-secondary--xs"
                  :disabled="!selectedSerialIds.length || deletingSelectedSerials"
                  @click="clearSelectedSerials"
                >
                  Clear
                </button>
                <button
                  type="button"
                  class="btn-danger btn-danger--xs"
                  :disabled="!selectedSerialIds.length || deletingSelectedSerials"
                  @click="deleteSelectedSerialRows"
                >
                  {{ deletingSelectedSerials ? 'Deleting…' : `Delete Selected (${selectedSerialIds.length})` }}
                </button>
                <div class="ml-auto text-slate-400">
                  {{ selectedVisibleSerialCount }} visible selected
                  <span v-if="selectedSerialIds.length !== selectedVisibleSerialCount">
                    · {{ selectedSerialIds.length }} total
                  </span>
                </div>
              </div>
              <div class="mt-4 border border-slate-800 rounded-lg overflow-hidden flex-1">
                <div
                  class="max-h-[55vh] overflow-auto pr-1 custom-scroll divide-y divide-slate-800/70"
                >
                  <div v-for="group in groupedSerials" :key="group.status" class="serial-group">
                    <header class="serial-group__header">
                      <span class="serial-group__badge" :class="statusPillClass(group.status)">{{
                        statusLabel(group.status)
                      }}</span>
                      <span class="serial-group__count">{{ group.items.length }}</span>
                    </header>
                    <div class="grid" :class="serialGridClass(group.items.length)">
                      <div
                        v-for="s in group.items"
                        :key="s.id"
                        class="serial-card"
                        :class="{ 'serial-card--selected': isSerialSelected(s.id) }"
                      >
                        <label class="serial-card__select" :aria-label="`Select serial ${safeSerial(s.serial)}`">
                          <input
                            type="checkbox"
                            class="form-checkbox form-checkbox--glass"
                            :checked="isSerialSelected(s.id)"
                            :disabled="deletingSelectedSerials"
                            @change="onSerialSelectionChange(s.id, $event)"
                          />
                        </label>
                        <div class="serial-card__content">
                          <div class="font-mono text-sm text-white truncate">
                            {{ safeSerial(s.serial) }}
                          </div>
                          <div class="text-[10px] text-slate-500 mt-1">
                            Updated {{ formatDate(s.updatedAt) }}
                          </div>
                        </div>
                        <select
                          class="serial-status-select"
                          v-model="serialStatusDraft[s.id]"
                          :disabled="statusSaving[s.id]"
                          @change="onSerialStatusChange(s, serialStatusDraft[s.id])"
                        >
                          <option v-for="option in statuses" :key="option" :value="option">
                            {{ statusLabel(option) }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="!serialsLoading && groupedSerials.length === 0"
                    class="text-slate-400 text-sm py-6 text-center"
                  >
                    No serials match.
                  </div>
                </div>
              </div>
            </section>

            <section class="glass-strong rounded-xl p-6 space-y-5">
              <div>
                <h4 class="text-white font-semibold text-sm uppercase tracking-wide">
                  Logistics &amp; Tech Data
                </h4>
                <p class="text-slate-400 text-xs mt-1">
                  Dimensions, weight, imagery, and datasheets.
                </p>
              </div>

              <div class="grid grid-cols-2 gap-3 text-xs text-slate-300">
                <div class="logistics-pill">
                  <span class="label">Weight</span>
                  <span class="value">{{ formatWeight(active?.specs?.shippingWeightOz) }}</span>
                </div>
                <div class="logistics-pill">
                  <span class="label">Height</span>
                  <span class="value">{{ formatDimension(active?.specs?.shippingHeightIn) }}</span>
                </div>
                <div class="logistics-pill">
                  <span class="label">Width</span>
                  <span class="value">{{ formatDimension(active?.specs?.shippingWidthIn) }}</span>
                </div>
                <div class="logistics-pill">
                  <span class="label">Depth</span>
                  <span class="value">{{ formatDimension(active?.specs?.shippingDepthIn) }}</span>
                </div>
              </div>

              <div class="grid gap-3">
                <div class="grid grid-cols-2 gap-3" v-if="frontBackImages.length">
                  <figure v-for="asset in frontBackImages" :key="asset.id" class="asset-frame">
                    <img
                      v-if="asset.filePath"
                      :src="asset.filePath"
                      :alt="asset.assetType + ' image'"
                      class="asset-img"
                    />
                    <div v-else class="asset-img placeholder-tile text-xs text-slate-500">
                      Missing image
                    </div>
                    <figcaption class="asset-caption">
                      {{ formatAssetLabel(asset.assetType) }}
                    </figcaption>
                  </figure>
                </div>
                <div v-else class="placeholder-tile">No imagery yet.</div>

                <div>
                  <h5 class="text-white text-xs uppercase tracking-wide mb-2">Datasheets</h5>
                  <div class="flex flex-wrap gap-2">
                    <template v-if="datasheetAssets.length">
                      <template v-for="asset in datasheetAssets" :key="asset.id ?? asset.filePath ?? asset.assetType">
                        <a
                          v-if="asset.filePath"
                          :href="asset.filePath"
                          target="_blank"
                          class="datasheet-chip"
                        >
                          {{ basename(asset.filePath) }}
                        </a>
                      </template>
                    </template>
                    <span v-else class="placeholder-chip">No datasheets</span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </Motion>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, reactive, watch } from 'vue'
import { Motion } from 'motion-v'
import { useInventoryStore } from '@/stores/inventory'
import type {
  EquipmentTypeSummary,
  EquipmentSerial,
  EquipmentStatus,
  EquipmentTypeAsset,
} from '@/types/inventory'
import { EQUIPMENT_STATUSES } from '@/types/inventory'
import {
  updateEquipmentType,
  createEquipmentSerial,
  updateEquipmentSerialStatus,
  deleteEquipmentSerials,
} from '@/lib/inventoryApi'

const inventory = useInventoryStore()
const DATA_UPDATED_EVENT = 'testdrive:data-updated'

const types = computed<EquipmentTypeSummary[]>(() => inventory.types)
const error = computed(() => inventory.typesError)
const typesLoading = computed(() => inventory.typesLoading)
const active = ref<EquipmentTypeSummary | null>(null)
const serials = ref<EquipmentSerial[]>([])
const filter = ref<'all' | EquipmentStatus>('all')
const query = ref('')
const serialsLoading = computed(() =>
  active.value ? (inventory.serialsLoading[active.value.id] ?? false) : false,
)
const serialError = computed(() =>
  active.value ? (inventory.serialsError[active.value.id] ?? null) : null,
)

function normalizeStatus(status: string): string {
  return status.toLowerCase().replace(/[_\s]+/g, '-')
}

const filteredSerials = computed(() => {
  let result = serials.value || []
  if (filter.value !== 'all') {
    result = result.filter((s) => normalizeStatus(s.status) === filter.value)
  }
  if (query.value.trim()) {
    const q = query.value.trim().toLowerCase()
    result = result.filter((s) => (s.serial ?? '').toLowerCase().includes(q))
  }
  return result
})

const groupedSerials = computed(() => {
  const groups: Record<string, EquipmentSerial[]> = {}
  filteredSerials.value.forEach((serial) => {
    const key = normalizeStatus(serial.status)
    if (!groups[key]) groups[key] = []
    groups[key].push(serial)
  })
  return Object.entries(groups)
    .map(([status, items]) => ({ status, items }))
    .sort((a, b) => statusOrder(a.status) - statusOrder(b.status))
})

const typeForm = reactive({
  name: '',
  descriptor: '',
  isAccessory: false,
  categoryId: '',
  products: '',
})
const editingType = ref(false)
const savingType = ref(false)
const typeFormError = ref<string | null>(null)

const serialForm = reactive({
  serial: '',
  status: EQUIPMENT_STATUSES[0] as EquipmentStatus,
  condition: '',
  notes: '',
})
const addingSerial = ref(false)
const savingSerial = ref(false)
const serialFormError = ref<string | null>(null)

const serialStatusDraft = ref<Record<number, EquipmentStatus>>({})
const statusSaving = ref<Record<number, boolean>>({})
const selectedSerialIds = ref<number[]>([])
const deletingSelectedSerials = ref(false)
const statuses = EQUIPMENT_STATUSES
const selectedSerialIdSet = computed(() => new Set(selectedSerialIds.value))
const filteredSerialIds = computed(() => filteredSerials.value.map((serial) => serial.id))
const allVisibleSerialsSelected = computed(
  () =>
    filteredSerialIds.value.length > 0 &&
    filteredSerialIds.value.every((serialId) => selectedSerialIdSet.value.has(serialId)),
)
const selectedVisibleSerialCount = computed(
  () => filteredSerialIds.value.filter((serialId) => selectedSerialIdSet.value.has(serialId)).length,
)

function open(type: EquipmentTypeSummary) {
  active.value = type
  selectedSerialIds.value = []
  if (type.id) {
    inventory.loadSerials(type.id)
  }
}

function close() {
  active.value = null
  serials.value = []
  selectedSerialIds.value = []
}

onMounted(() => {
  if (!inventory.types.length && !inventory.typesLoading) {
    void inventory.loadTypes()
  }
  window.addEventListener(DATA_UPDATED_EVENT, onDataUpdatedEvent)
})

onBeforeUnmount(() => {
  window.removeEventListener(DATA_UPDATED_EVENT, onDataUpdatedEvent)
})

function onDataUpdatedEvent() {
  void inventory.loadTypes()
  if (active.value?.id) {
    void inventory.loadSerials(active.value.id, true)
  }
}

const frontBackImages = computed(() =>
  (active.value?.assets ?? []).filter(
    (asset) => asset.assetType === 'front_image' || asset.assetType === 'back_image',
  ),
)
const datasheetAssets = computed(() =>
  (active.value?.assets ?? []).filter((asset) => asset.assetType === 'datasheet'),
)

function statusLabel(status: string) {
  return status.replace('-', ' ')
}

function statusOrder(status: string) {
  return STATUSES_ORDER.indexOf(status as EquipmentStatus)
}

function statusPillClass(status: string) {
  const key = normalizeStatus(status)
  return {
    'pill-available': key === 'available',
    'pill-reserved': key === 'reserved',
    'pill-checked': key === 'checked-out',
    'pill-maintenance': key === 'maintenance',
  }
}

const STATUSES_ORDER: EquipmentStatus[] = [
  'available',
  'reserved',
  'checked-out',
  'maintenance',
  'retired',
  'lost',
]

function formatDate(value: string) {
  const dt = new Date(value)
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString()
}

function formatWeight(weight?: number | null) {
  if (weight == null) return '—'
  return `${weight.toFixed(1)} oz`
}

function formatDimension(value?: number | null) {
  if (value == null) return '—'
  return `${value.toFixed(1)}"`
}

function formatAssetLabel(type: EquipmentTypeAsset['assetType']) {
  if (type === 'front_image') return 'Front'
  if (type === 'back_image') return 'Back'
  if (type === 'datasheet') return 'Datasheet'
  return type
}

function basename(path: string | null | undefined) {
  if (!path) return 'Untitled asset'
  return path.split('/').pop() ?? path
}

const serialGridClass = (length: number) =>
  length > 6 ? 'grid-cols-1 sm:grid-cols-2 gap-3 p-4' : 'grid-cols-1 gap-3 p-4'

function setFilter(status: 'all' | EquipmentStatus) {
  filter.value = status
}

function chipClass(status: 'all' | EquipmentStatus) {
  const active = filter.value === status
  return [
    'px-3 py-1 rounded-full transition-colors border',
    active
      ? 'bg-primary-600/20 border-primary-500/40 text-primary-200'
      : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:text-white',
  ]
}

const safeSerial = (serial: string | null | undefined) => serial ?? '—'

function syncTypeForm(type: EquipmentTypeSummary | null) {
  if (!type) return
  typeForm.name = type.name
  typeForm.descriptor = type.descriptor ?? ''
  typeForm.isAccessory = type.isAccessory
  typeForm.categoryId = type.categoryId != null ? String(type.categoryId) : ''
  typeForm.products = type.products.join(', ')
}

function syncSerialDraft(list: EquipmentSerial[]) {
  const draft: Record<number, EquipmentStatus> = {}
  list.forEach((serial) => {
    draft[serial.id] = serial.status
  })
  serialStatusDraft.value = draft
}

function isSerialSelected(serialId: number) {
  return selectedSerialIdSet.value.has(serialId)
}

function toggleSerialSelection(serialId: number, checked: boolean) {
  const next = new Set(selectedSerialIds.value)
  if (checked) next.add(serialId)
  else next.delete(serialId)
  selectedSerialIds.value = [...next]
}

function onSerialSelectionChange(serialId: number, event: Event) {
  const input = event.target as HTMLInputElement | null
  toggleSerialSelection(serialId, Boolean(input?.checked))
}

function onSelectAllVisibleChange(event: Event) {
  const input = event.target as HTMLInputElement | null
  const checked = Boolean(input?.checked)
  const next = new Set(selectedSerialIds.value)
  filteredSerialIds.value.forEach((serialId) => {
    if (checked) next.add(serialId)
    else next.delete(serialId)
  })
  selectedSerialIds.value = [...next]
}

function clearSelectedSerials() {
  selectedSerialIds.value = []
}

function notify(message: string, type: 'success' | 'error' = 'success') {
  try {
    window.dispatchEvent(new CustomEvent('toast', { detail: { type, message } }))
  } catch {}
}

function startEditType() {
  if (!active.value) return
  syncTypeForm(active.value)
  typeFormError.value = null
  editingType.value = true
}

function cancelEditType() {
  editingType.value = false
}

async function saveType() {
  if (!active.value) return
  const name = typeForm.name.trim()
  if (!name) {
    typeFormError.value = 'Name is required'
    return
  }
  const descriptor = typeForm.descriptor.trim() || null
  const categoryId = typeForm.categoryId ? Number(typeForm.categoryId) : null
  if (typeForm.categoryId && Number.isNaN(Number(typeForm.categoryId))) {
    typeFormError.value = 'Category ID must be numeric'
    return
  }
  const products = typeForm.products
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
  savingType.value = true
  typeFormError.value = null
  try {
    await updateEquipmentType(active.value.id, {
      name,
      descriptor,
      isAccessory: typeForm.isAccessory,
      categoryId,
      products,
    })
    await inventory.loadTypes()
    const updated = inventory.typeById[active.value.id]
    if (updated) {
      active.value = updated
      syncTypeForm(updated)
    }
    editingType.value = false
    notify('Equipment type updated')
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    typeFormError.value = message
    notify(message, 'error')
  } finally {
    savingType.value = false
  }
}

function resetSerialForm() {
  serialForm.serial = ''
  serialForm.status = EQUIPMENT_STATUSES[0]
  serialForm.condition = ''
  serialForm.notes = ''
}

function startAddSerial() {
  if (!active.value) return
  resetSerialForm()
  serialFormError.value = null
  addingSerial.value = true
}

function cancelAddSerial() {
  addingSerial.value = false
}

async function submitSerial() {
  if (!active.value) return
  const serialValue = serialForm.serial.trim()
  if (!serialValue) {
    serialFormError.value = 'Serial number is required'
    return
  }
  savingSerial.value = true
  serialFormError.value = null
  try {
    await createEquipmentSerial({
      equipmentTypeId: active.value.id,
      serial: serialValue,
      status: serialForm.status,
      condition: serialForm.condition.trim() || null,
      notes: serialForm.notes.trim() || null,
    })
    await inventory.loadSerials(active.value.id, true)
    await inventory.loadTypes()
    const latest = inventory.getSerials(active.value.id)
    serials.value = latest
    syncSerialDraft(latest)
    notify('Serial added')
    addingSerial.value = false
    resetSerialForm()
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    serialFormError.value = message
    notify(message, 'error')
  } finally {
    savingSerial.value = false
  }
}

async function onSerialStatusChange(serial: EquipmentSerial, newStatus: EquipmentStatus) {
  if (newStatus === serial.status) return
  statusSaving.value = { ...statusSaving.value, [serial.id]: true }
  try {
    await updateEquipmentSerialStatus(serial.id, {
      fromStatus: serial.status,
      toStatus: newStatus,
    })
    await inventory.loadSerials(serial.equipmentTypeId, true)
    await inventory.loadTypes()
    const latest = inventory.getSerials(serial.equipmentTypeId)
    serials.value = latest
    syncSerialDraft(latest)
    notify('Serial status updated')
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    serialStatusDraft.value = { ...serialStatusDraft.value, [serial.id]: serial.status }
    notify(message, 'error')
  } finally {
    statusSaving.value = { ...statusSaving.value, [serial.id]: false }
  }
}

/**
 * Bulk delete is intentionally filter-aware so operators can quickly clear
 * scanned test serials from the current Inventory view without leaving the drawer.
 */
async function deleteSelectedSerialRows() {
  if (!active.value || !selectedSerialIds.value.length) return

  const selectedItems = serials.value.filter((serial) => selectedSerialIdSet.value.has(serial.id))
  if (!selectedItems.length) {
    selectedSerialIds.value = []
    return
  }

  const selectedCount = selectedItems.length
  const nonAvailableCount = selectedItems.filter((serial) => normalizeStatus(serial.status) !== 'available').length
  const confirmMessage = [
    `Delete ${selectedCount} selected serial${selectedCount === 1 ? '' : 's'} from ${active.value.name}?`,
    nonAvailableCount
      ? `${nonAvailableCount} selected serial${nonAvailableCount === 1 ? ' is' : 's are'} not in Available status.`
      : 'This action permanently removes the selected serial rows.',
  ].join('\n\n')

  if (typeof window !== 'undefined' && !window.confirm(confirmMessage)) return

  deletingSelectedSerials.value = true
  try {
    await deleteEquipmentSerials(selectedItems.map((serial) => serial.id))
    await inventory.loadSerials(active.value.id, true)
    await inventory.loadTypes()
    const latest = inventory.getSerials(active.value.id)
    serials.value = latest
    selectedSerialIds.value = []
    notify(`${selectedCount} serial${selectedCount === 1 ? '' : 's'} deleted`)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    notify(message, 'error')
  } finally {
    deletingSelectedSerials.value = false
  }
}

watch(active, (value) => {
  if (!value) {
    selectedSerialIds.value = []
    return
  }
  syncTypeForm(value)
  inventory.loadSerials(value.id).then(() => {
    serials.value = inventory.getSerials(value.id)
  })
})

watch(serials, (list) => {
  syncSerialDraft(list)
  const serialIds = new Set(list.map((serial) => serial.id))
  selectedSerialIds.value = selectedSerialIds.value.filter((serialId) => serialIds.has(serialId))
})
</script>

<style scoped>
.mini-stat {
  border-radius: 0.75rem;
  border: 1px solid rgba(30, 41, 59, 0.7);
  background: rgba(15, 23, 42, 0.6);
  padding: 0.5rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.mini-stat dt {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: rgba(148, 163, 184, 0.75);
}

.mini-stat dd {
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
}

.mini-stat--available {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.3);
}

.mini-stat--checked {
  background: rgba(56, 189, 248, 0.1);
  border-color: rgba(56, 189, 248, 0.3);
}

.mini-stat--reserved {
  background: rgba(251, 191, 36, 0.1);
  border-color: rgba(251, 191, 36, 0.3);
}

.mini-stat--maintenance {
  background: rgba(244, 114, 182, 0.1);
  border-color: rgba(244, 114, 182, 0.3);
}

.mini-stat--total {
  background: rgba(168, 85, 247, 0.1);
  border-color: rgba(168, 85, 247, 0.3);
}

.input-dark {
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(51, 65, 85, 0.8);
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  color: rgba(226, 232, 240, 0.95);
  font-size: 0.875rem;
}

.input-dark:focus {
  outline: none;
  box-shadow: 0 0 0 1px rgba(96, 165, 250, 0.6);
}

.form-checkbox--glass {
  accent-color: rgba(59, 130, 246, 0.8);
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(51, 65, 85, 0.8);
}

.btn-secondary {
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid rgba(71, 85, 105, 0.85);
  color: rgba(226, 232, 240, 0.85);
  background: transparent;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease;
}

.btn-secondary:hover {
  background: rgba(30, 41, 59, 0.8);
}

.btn-secondary--xs {
  font-size: 0.75rem;
}

.btn-primary {
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  background: rgba(14, 165, 233, 0.95);
  color: #fff;
  transition: background 0.2s ease;
}

.btn-primary:hover {
  background: rgba(56, 189, 248, 0.95);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary--xs {
  font-size: 0.75rem;
}

.btn-danger {
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  background: rgba(220, 38, 38, 0.95);
  color: #fff;
  transition: background 0.2s ease;
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.95);
}

.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-danger--xs {
  font-size: 0.75rem;
}

.serial-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid rgba(30, 41, 59, 0.7);
  border-radius: 0.75rem;
  padding: 0.75rem;
  background: rgba(15, 23, 42, 0.7);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.serial-card--selected {
  border-color: rgba(96, 165, 250, 0.58);
  background: rgba(15, 23, 42, 0.9);
  box-shadow:
    inset 0 0 0 1px rgba(96, 165, 250, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.serial-card__select {
  flex: 0 0 auto;
  align-self: center;
}

.serial-card__content {
  min-width: 0;
  flex: 1 1 auto;
}

.serial-status-select {
  background: rgba(2, 6, 23, 0.85);
  border: 1px solid rgba(51, 65, 85, 0.9);
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  color: rgba(226, 232, 240, 0.85);
}
.custom-scroll {
  scrollbar-width: thin;
}
.custom-scroll::-webkit-scrollbar {
  width: 6px;
}
.custom-scroll::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.35);
  border-radius: 999px;
}

.logistics-pill {
  border-radius: 0.75rem;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(30, 41, 59, 0.8);
  padding: 0.5rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.logistics-pill .label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: rgba(100, 116, 139, 0.75);
}

.logistics-pill .value {
  font-size: 0.875rem;
  color: rgba(226, 232, 240, 0.9);
}

.asset-frame {
  border-radius: 0.75rem;
  overflow: hidden;
  border: 1px solid rgba(30, 41, 59, 0.8);
  background: rgba(15, 23, 42, 0.7);
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.55);
}
.asset-img {
  width: 100%;
  height: 8rem;
  object-fit: cover;
  background: #0f172a;
}

.asset-caption {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  text-align: center;
  padding: 0.25rem 0.5rem;
  background: rgba(15, 23, 42, 0.8);
  color: rgba(148, 163, 184, 0.75);
}
.placeholder-tile {
  border: 1px dashed rgba(71, 85, 105, 0.7);
  border-radius: 0.75rem;
  padding: 1.5rem;
  text-align: center;
  font-size: 0.75rem;
  color: rgba(100, 116, 139, 0.7);
}

.datasheet-chip {
  padding: 0.25rem 0.75rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(59, 130, 246, 0.5);
  color: rgba(191, 219, 254, 0.85);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  background: rgba(59, 130, 246, 0.12);
  transition: background 0.2s ease;
}

.datasheet-chip:hover {
  background: rgba(59, 130, 246, 0.2);
}

.placeholder-chip {
  padding: 0.25rem 0.75rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(71, 85, 105, 0.8);
  color: rgba(148, 163, 184, 0.75);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.2em;
}

.serial-card select option {
  background: #0f172a;
}

.serial-group {
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.serial-group + .serial-group {
  border-top: 1px solid rgba(30, 41, 59, 0.6);
}
.serial-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.serial-group__badge {
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}
.serial-group__count {
  font-size: 0.75rem;
  color: rgba(148, 163, 184, 0.65);
}
.pill-available {
  background: rgba(16, 185, 129, 0.14);
  color: rgba(16, 185, 129, 0.9);
  border: 1px solid rgba(16, 185, 129, 0.3);
}
.pill-reserved {
  background: rgba(251, 191, 36, 0.12);
  color: rgba(250, 204, 21, 0.85);
  border: 1px solid rgba(250, 204, 21, 0.32);
}
.pill-checked {
  background: rgba(56, 189, 248, 0.12);
  color: rgba(56, 189, 248, 0.88);
  border: 1px solid rgba(56, 189, 248, 0.32);
}
.pill-maintenance {
  background: rgba(244, 114, 182, 0.12);
  color: rgba(244, 114, 182, 0.85);
  border: 1px solid rgba(244, 114, 182, 0.32);
}
</style>

<style scoped>
.veil-enter-active,
.veil-leave-active {
  transition: opacity 0.18s ease;
}
.veil-enter-from,
.veil-leave-to {
  opacity: 0;
}
.modal-pop-enter-active {
  animation: pop-in 0.22s cubic-bezier(0.2, 0.8, 0.2, 1.2);
}
.modal-pop-leave-active {
  animation: pop-out 0.15s ease forwards;
}
@keyframes pop-in {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
@keyframes pop-out {
  to {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }
}
</style>
